import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartFrame } from '../../../components/chart/chart-frame';
import { FLOW_VAR, RAG_VAR } from '../../../components/chart/rag';
import { errorMessage } from '../../../lib/error-message';
import { type ReportRange, reportApi, reportKeys } from '../api';
import { displayDate } from '../../../lib/date';

/** Bao nhiêu task trễ nhất được liệt kê dưới biểu đồ. */
const WORST_VISIBLE = 5;

/**
 * Độ trễ tích luỹ so với baseline — spec 16.3.
 *
 * Hình dáng đường mới là thông tin, không phải độ cao: **đi lên đều** nghĩa là
 * có việc quá hạn mà chưa ai động tới, còn **bậc thang đứng yên** nghĩa là đã
 * trễ nhưng đã xong. Hai tình huống đó cần hai cách xử lý khác nhau, nên biểu
 * đồ phải phân biệt được chúng.
 *
 * Danh sách "trễ nhiều nhất" nằm ngay dưới: câu hỏi tiếp theo sau khi thấy
 * đường đi lên luôn là "việc nào".
 */
export function SlippageChart({
  projectId,
  range,
}: {
  projectId: string;
  range: ReportRange;
}): ReactNode {
  const { t } = useTranslation();

  const report = useQuery({
    queryKey: reportKeys.slippage(projectId, range),
    queryFn: () => reportApi.slippage(projectId, range),
  });

  const buckets = report.data?.buckets ?? [];
  const baselineSetAt = report.data?.baselineSetAt ?? null;

  return (
    <ChartFrame
      title={t('reports.slippageTitle')}
      help="slippage"
      subtitle={
        baselineSetAt === null
          ? t('reports.slippageNoBaseline')
          : t('reports.slippageSubtitle', { at: displayDate(baselineSetAt) })
      }
      asOf={range.asOf ? new Date(range.asOf) : undefined}
      loading={report.isPending}
      error={report.isError ? errorMessage(report.error, t) : null}
      onRetry={() => void report.refetch()}
      empty={
        report.data && buckets.every((bucket) => bucket.lateTaskCount === 0)
          ? { title: t('reports.slippageEmpty'), description: t('reports.slippageEmptyHelp') }
          : null
      }
      legend={[
        { label: t('reports.slippageDays'), color: RAG_VAR.red },
        { label: t('reports.lateTasks'), color: FLOW_VAR.inProgress },
      ]}
    >
      <div className="flex flex-col gap-2">
        <div className="h-44 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={buckets}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="var(--color-border)" />
              <YAxis yAxisId="days" tick={{ fontSize: 11 }} stroke="var(--color-border)" />
              <YAxis
                yAxisId="count"
                orientation="right"
                allowDecimals={false}
                tick={{ fontSize: 11 }}
                stroke="var(--color-border)"
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--color-background)',
                  border: '1px solid var(--color-border)',
                  fontSize: 12,
                }}
              />
              <Bar
                yAxisId="count"
                dataKey="lateTaskCount"
                name={t('reports.lateTasks')}
                fill={FLOW_VAR.inProgress}
                fillOpacity={0.35}
              />
              <Line
                yAxisId="days"
                type="monotone"
                dataKey="slippageDays"
                name={t('reports.slippageDays')}
                stroke={RAG_VAR.red}
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {(report.data?.worst.length ?? 0) > 0 && (
          <div className="flex flex-col gap-0.5">
            <p className="text-xs font-medium">{t('reports.slippageWorst')}</p>
            {report.data!.worst.slice(0, WORST_VISIBLE).map((task) => (
              <div key={task.taskId} className="flex items-baseline gap-2 text-xs">
                <span className="font-mono text-muted-foreground">{task.taskKey}</span>
                <span className="truncate">{task.title}</span>
                <span className="ml-auto shrink-0 text-rag-red">
                  {t('reports.slippageDays')}: {task.slippageDays}
                </span>
                {task.completedOn === null && (
                  <span className="shrink-0 text-muted-foreground">
                    {t('reports.slippageStillOpen')}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </ChartFrame>
  );
}
